import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_GROW_ANIMATION } from '../chartAnimation';
import { ClrBarHover } from '../chartMarkHover';
import ClrRechartsTooltip from '../ClrRechartsTooltip';
import {
  categoryTickLabel,
  isCategoryPadPayload,
  padCategoriesToBottom,
} from '../padCategoryAxis';

const BAR_COLOR = '#26735C';
const TRACK_COLOR = '#e8efe9';
const MIN_CATEGORY_SLOTS = 5;

function ApprovalRateBarBackground(props) {
  if (props?.payload?.__pad) return null;
  const {
    x, y, width, height,
  } = props;
  if (x == null || y == null || width == null || height == null) return null;
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={TRACK_COLOR}
    />
  );
}

ApprovalRateBarBackground.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  payload: PropTypes.object,
};

function ApprovalRateByPayerCard({ data }) {
  const chartData = useMemo(() => (data || []).map((row) => ({
    payer: row.payer_name || row.payerName || 'Unknown',
    rate: row.approval_rate_pct ?? row.approvalRatePct ?? 0,
  })), [data]);

  const plotData = useMemo(
    () => padCategoriesToBottom(chartData, {
      minSlots: MIN_CATEGORY_SLOTS,
      categoryKey: 'payer',
      valueKeys: ['rate'],
    }),
    [chartData],
  );

  return (
    <div className="clr-chart-card">
      <div className="clr-chart-card-header clr-chart-card-header-accent">
        <h3 className="clr-chart-card-title">Approval rate by payer</h3>
      </div>
      <div className="clr-chart-card-body">
        {chartData.length === 0 ? (
          <p className="clr-chart-plot clr-chart-empty">No payer data</p>
        ) : (
          <div className="clr-chart-plot">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={plotData}
                margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e0e4e0" />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  height={24}
                  tickMargin={4}
                  tick={{ fontSize: 11, fill: '#6b7a74' }}
                  axisLine={{ stroke: '#e0e4e0' }}
                  tickLine={false}
                  tickFormatter={(v) => `${v}%`}
                />
                <YAxis
                  type="category"
                  dataKey="payer"
                  width={90}
                  tick={{ fontSize: 11, fill: '#3e4f4c' }}
                  tickFormatter={categoryTickLabel}
                  axisLine={false}
                  tickLine={false}
                />
                <ClrRechartsTooltip
                  cursor={{ fill: 'rgba(62, 79, 76, 0.04)' }}
                  formatter={(payload) => {
                    if (isCategoryPadPayload(payload)) return { title: null, rows: [] };
                    const row = payload[0]?.payload;
                    return {
                      title: row?.payer || '',
                      rows: [{
                        key: 'rate',
                        label: 'Approval rate',
                        value: `${row?.rate ?? payload[0]?.value ?? 0}%`,
                      }],
                    };
                  }}
                />
                <Bar
                  dataKey="rate"
                  fill={BAR_COLOR}
                  activeBar={ClrBarHover}
                  background={ApprovalRateBarBackground}
                  radius={[0, 0, 0, 0]}
                  barSize={35.794525146484375}
                  maxBarSize={35.794525146484375}
                  {...CHART_GROW_ANIMATION}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

ApprovalRateByPayerCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    payer_name: PropTypes.string,
    payerName: PropTypes.string,
    approval_rate_pct: PropTypes.number,
    approvalRatePct: PropTypes.number,
  })),
};

export default ApprovalRateByPayerCard;
